import React, { useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Switch,
  Input,
} from '@heroui/react';
import { ResponsiveModal, Typography } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
import AlertBlock from '../../../../components/alert-block';
import CustomHeroButton from '../../../../components/button/index';
import { OrderData } from '../types';

interface EditOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: OrderData | null;
  onSave?: (orderData: any) => void;
}

const EditOrderModal: React.FC<EditOrderModalProps> = ({ isOpen, onClose, order, onSave }) => {
  const { t } = useTranslations();
  const [autoRenewal, setAutoRenewal] = useState(false);
  const [fansCount, setFansCount] = useState(order?.offer.subscribers.toString() || '50');
  const [maxPrice, setMaxPrice] = useState(order?.offer.pricePerSubscriber.toString() || '2.3');
  const [hideStatistics, setHideStatistics] = useState(false);

  const handleSave = () => {
    const orderData = {
      autoRenewal,
      fansCount: parseInt(fansCount),
      maxPrice: parseFloat(maxPrice),
      hideStatistics,
    };

    if (onSave) {
      onSave(orderData);
    }
    onClose();
  };

  if (!order) return null;

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      scrollBehavior="inside"
      classNames={{
        base: 'max-h-[90vh]',
        wrapper: 'z-[9999]',
        backdrop: 'z-[9998]',
      }}
    >
      <ModalContent className="md:w-[37.5rem] md:max-w-[37.5rem]">
        <ModalHeader className="flex flex-col gap-1 px-4 md:px-6 py-4 border-b border-gray-200">
          <Typography variant="h6" className="font-medium text-xl leading-6">
            {t('orders.editModal.title', { id: order.internalId })}
          </Typography>
        </ModalHeader>

        <ModalBody className="px-4 md:px-6 py-5 space-y-6">
          {/* Автопродление */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Typography variant="body2">{t('orders.editModal.autoRenewal')}</Typography>
              <Switch
                isSelected={autoRenewal}
                onValueChange={setAutoRenewal}
                size="sm"
                classNames={{}}
                color="primary"
              />
            </div>

            <Typography variant="body3" className="text-auth-muted text-xxs leading-4">
              {t('orders.editModal.autoRenewalDescription')}
            </Typography>
          </div>

          {/* Поля ввода */}
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                label={t('orders.editModal.fans')}
                labelPlacement="outside"
                value={fansCount}
                onValueChange={setFansCount}
                type="number"
                size="sm"
                variant="bordered"
                classNames={{
                  input: 'text-sm bg-white',
                  label: 'text-sm font-medium text-auth-ink',
                  inputWrapper:
                    'h-10 bg-white border border-auth-strength-none rounded-lg hover:border-auth-strength-none focus-within:border-auth-strength-none',
                }}
              />
            </div>
            <div className="flex-1">
              <Input
                label={t('orders.editModal.maxPrice')}
                labelPlacement="outside"
                value={maxPrice}
                onValueChange={setMaxPrice}
                type="number"
                size="sm"
                variant="bordered"
                classNames={{
                  input: 'text-sm bg-white',
                  label: 'text-sm font-medium text-auth-ink',
                  inputWrapper:
                    'h-10 bg-white border border-auth-strength-none rounded-lg hover:border-auth-strength-none focus-within:border-auth-strength-none',
                }}
              />
            </div>
          </div>

          {/* Информационные блоки */}
          <div className="space-y-4">
            <AlertBlock
              data={[
                {
                  severity: 'info',
                  title: t('orders.editModal.purchaseTerms.title'),
                  description: t('orders.editModal.purchaseTerms.description'),
                },
              ]}
            />

            <AlertBlock
              data={[
                {
                  severity: 'info',
                  title: t('orders.editModal.renewalInfo.title'),
                },
              ]}
              addContent={
                <div className="">
                  <Typography variant="body2" className="text-auth-muted leading-5">
                    {t('orders.editModal.renewalInfo.currentPrice', { fansCount, maxPrice })}
                  </Typography>
                  <Typography variant="body2" className="text-auth-muted leading-5">
                    {t('orders.editModal.renewalInfo.commission')}
                  </Typography>
                  <Typography variant="body2" className="text-auth-muted leading-5">
                    {t('orders.editModal.renewalInfo.totalCost', {
                      total: (parseInt(fansCount) * parseFloat(maxPrice) * 1.17).toFixed(2),
                    })}
                  </Typography>
                  <br />
                  <Typography variant="body2" className="text-auth-muted leading-5">
                    {t('orders.editModal.renewalInfo.balanceNote')}
                  </Typography>
                </div>
              }
            />
          </div>

          {/* Скрыть статистику доходов */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Typography variant="body2">{t('orders.editModal.hideStatistics')}</Typography>
              <Switch
                isSelected={hideStatistics}
                onValueChange={setHideStatistics}
                size="sm"
                classNames={{}}
                color="primary"
              />
            </div>

            <Typography variant="body3" className="text-auth-muted text-xxs leading-4">
              {t('orders.editModal.hideStatisticsDescription')}
            </Typography>
          </div>
        </ModalBody>

        <ModalFooter className="px-4 md:px-6 py-4 border-t border-gray-200 gap-3">
          <CustomHeroButton
            title={t('orders.editModal.send')}
            onPress={handleSave}
            variant="primary"
            className="px-4"
          />
        </ModalFooter>
      </ModalContent>
    </ResponsiveModal>
  );
};

export default EditOrderModal;
